import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useReportLoadFailure } from "@/hooks/useReportLoadFailure";
import { isSupplierMatchCheckEligible } from "@/lib/supplier-match-status";
import {
  useCheckSupplierMatches,
  useSupplierMatches
} from "@/queries/customer/supplier-match";
import { memo, useEffect, useRef, useState } from "react";

import { SupplierMatchCard } from "./SupplierMatchCard";
import { SupplierMatchDetailDialog } from "./SupplierMatchDetailDialog";

/**
 * The Supplier Match grid (ADR-0005 "Page layout") - one card per active
 * seller, reused identically for both the account-settings page and the home
 * dashboard's onboarding invite link. Every seller always lists, capable or
 * not (ADR-0005 "Supplier scope") - there is no empty state.
 *
 * Auto-checks every not-yet-settled, lookup-capable seller once on mount
 * (ADR-0005 "Matching runs automatically") - on every visit to the grid, so a
 * salon can come back to retry a lookup that did not settle. The Order
 * assistant's trade-account card runs the same check from a different place,
 * at most once per page load (`useSupplierMatchAutoCheck`).
 */
export const SupplierMatchGridView = memo(function SupplierMatchGridView() {
  const { data, isLoading, error } = useSupplierMatches();
  useReportLoadFailure(error, "supplier match grid");
  const checkMatches = useCheckSupplierMatches();
  const [selectedSellerId, setSelectedSellerId] = useState<string | null>(null);

  // One auto-check per mounted grid. Deliberately NOT paired with a
  // cancellation flag - see EnterPage.tsx's identical fire-once pattern for
  // why that would strand StrictMode's double-invoke in a stuck state.
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;
    checkMatches.mutate();
  }, []);

  const matches = data?.supplier_matches ?? [];
  const selectedView =
    matches.find((match) => match.seller_id === selectedSellerId) ?? null;

  return (
    <div>
      <header className="border-border border-b p-6">
        <h2
          className="text-xl font-semibold text-gray-900 dark:text-white"
          id="supplier-match-heading"
        >
          Supplier Match
        </h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          See which suppliers recognize your account and get your negotiated
          prices automatically.
        </p>
      </header>

      <section aria-labelledby="supplier-match-heading" className="p-6">
        {isLoading ? (
          <div
            className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4"
            aria-hidden="true"
          >
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Card key={i}>
                <CardContent className="flex flex-col items-center gap-2 p-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-5 w-16 rounded-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : error ? (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-400"
          >
            We couldn&apos;t load your suppliers. Please try again.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {matches.map((match) => (
              <SupplierMatchCard
                key={match.seller_id}
                view={match}
                checking={
                  checkMatches.isPending && isSupplierMatchCheckEligible(match)
                }
                onSelect={setSelectedSellerId}
              />
            ))}
          </div>
        )}
      </section>

      <SupplierMatchDetailDialog
        view={selectedView}
        open={!!selectedSellerId}
        onOpenChange={(open) => {
          if (!open) setSelectedSellerId(null);
        }}
      />
    </div>
  );
});
